import moment from 'moment';
import {
	COMPLIANCE_VERDICT_STATUS_APPEARANCE,
	COMPLIANCE_VERDICT_STATUS_LABELS,
	DEFAULT_APPEARANCE,
	OVERRIDE_SCOPES
} from '../../../../constants/compliance-verdicts';
import { NOT_AVAILABLE } from '../../../../constants/hardware-inventory';
import { scopeOf } from '../../../../data/complianceVerdict/mapper';
import { findComponentTypeById } from '../../../../helpers/hardwareInventory';

/*
 * How a compliance verdict reads on screen: what it covers, its status badge and its dates.
 *
 * `moment` is imported explicitly rather than used as the webpack ProvidePlugin global: that
 * global, like `_`, is not defined when mocha loads this module, so anything reachable from a
 * test must not rely on it.
 */

/*
 * An absent component type id is a meaning, not missing data - it is how the API expresses an
 * appliance-wide waiver. Rendering the usual "not available" dash here would read as "we do not
 * know what this covers" about the override with the widest possible scope.
 *
 * `componentTypesLoading` is a parameter rather than something inferred from an empty catalog
 * because the catalog is fetched independently of the verdicts. Calling an id "unknown" because
 * it is missing from a catalog that has not arrived yet is a false statement, so while the catalog
 * is in flight the bare id is shown with no claim attached.
 */
export const verdictScopeLabel = (verdict, componentTypes, componentTypesLoading) => {
	const componentTypeId = verdict?.hardwareComponentTypeId;

	if (!componentTypeId) {
		return 'Whole appliance';
	}

	const componentType = findComponentTypeById(componentTypeId, componentTypes);

	if (componentType?.displayName) {
		return componentType.displayName;
	}

	/*
	 * The catalog is an inner join on the fleet analytics table, so a type no appliance currently
	 * reports is legitimately absent from it. The id is the fallback - never render this blank,
	 * since it is the only thing identifying the verdict in a support conversation.
	 */
	if (componentTypesLoading) {
		return componentTypeId;
	}

	return `${componentTypeId} (unknown component type)`;
};

/*
 * "Any version" rather than a dash for a component-type override: the version column is empty
 * because the waiver covers every version, which is the opposite of unknown. The dash is kept for
 * an appliance-wide override, where the field genuinely does not apply.
 */
export const verdictVersionLabel = (verdict) => {
	const scope = scopeOf(verdict);

	if (scope === OVERRIDE_SCOPES.COMPONENT_VERSION) {
		return verdict.firmwareVersion;
	}

	if (scope === OVERRIDE_SCOPES.COMPONENT_TYPE) {
		return 'Any version';
	}

	return '—';
};

/*
 * The blast radius of the override being composed, restated as a sentence from the values actually
 * chosen. The radio labels say what each scope is; this says what the override in front of the
 * operator will waive.
 *
 * Takes the form's two fields rather than a verdict record, unlike its siblings above: nothing
 * has been granted yet, and the summary has to move with every keystroke of the choice.
 *
 * A stand-in is used for a name the catalog cannot supply - no type picked yet, a catalog still
 * loading, or a type absent from it. Naming the bare id here would put a support-conversation
 * identifier in the middle of an English sentence, and the picker above already shows which type is
 * selected. The stand-in differs per sentence only so that both read: "every component of the
 * chosen type" and "only the chosen component type" are the same fallback in two grammars.
 */
export const overrideScopeSummary = (scope, hardwareComponentTypeId, componentTypes) => {
	if (scope === OVERRIDE_SCOPES.APPLIANCE) {
		return 'Waives the compliance check for every component on this appliance, on any firmware version.';
	}

	const componentType = findComponentTypeById(hardwareComponentTypeId, componentTypes);

	if (scope === OVERRIDE_SCOPES.COMPONENT_TYPE) {
		const named = componentType?.displayName ?? 'component of the chosen type';

		return `Waives the compliance check for every ${named} on this appliance, on any firmware version.`;
	}

	const named = componentType?.displayName ?? 'the chosen component type';

	return `Waives the compliance check only for ${named} running that exact firmware version on this appliance.`;
};

/*
 * The one date format for the verdict table and the revoke dialog, so the two cannot drift apart.
 */
export const verdictDateLabel = (value) => {
	return value ? moment(value).format('DD/MM/YYYY HH:mm') : NOT_AVAILABLE;
};

export const statusAppearance = (status) => {
	return COMPLIANCE_VERDICT_STATUS_APPEARANCE[status] ?? DEFAULT_APPEARANCE;
};

/*
 * An unrecognised status is shown raw rather than hidden: a status the API adds before this page
 * learns it is still the truth about the row.
 */
export const statusLabel = (status) => {
	return COMPLIANCE_VERDICT_STATUS_LABELS[status] ?? status;
};
